import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import api from '../services/api.ts';
import { applyCurrencyMask, parseCurrency } from '../utils/currency.ts';
import FormPage from '../components/ui/FormPage.tsx';

const CreateRentalGlobal: React.FC = () => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [assetId, setAssetId] = useState('');
  const [tenantName, setTenantName] = useState('');
  const [tenantEmail, setTenantEmail] = useState('');
  const [tenantPhone, setTenantPhone] = useState('');
  const [monthlyRentDisplay, setMonthlyRentDisplay] = useState('');
  const [paymentDay, setPaymentDay] = useState('5');
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { data: assets } = useQuery('assets-for-rental', async () => (await api.get('/assets')).data);

  const mutation = useMutation(
    async () => {
      const payload = {
        tenantName,
        tenantEmail: tenantEmail || undefined,
        tenantPhone: tenantPhone || undefined,
        monthlyRent: parseCurrency(monthlyRentDisplay),
        paymentDay: Number(paymentDay) || null,
        startDate,
        status: 'active',
      };
      const res = await api.post(`/assets/${assetId}/rentals`, payload);
      return res.data;
    },
    {
      onSuccess: () => {
        qc.invalidateQueries('rentals');
        navigate('/rentals');
      },
      onError: () => setErrors({ submit: 'Erro ao criar aluguel. Tente novamente.' }),
    }
  );

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const next: Record<string, string> = {};
    if (!assetId) next.assetId = 'Selecione o bem.';
    if (!tenantName.trim()) next.tenantName = 'Informe o inquilino.';
    if (!monthlyRentDisplay) next.monthlyRent = 'Informe o valor mensal.';
    if (!startDate) next.startDate = 'Informe a data de início.';
    setErrors(next);
    if (Object.keys(next).length) return;
    mutation.mutate();
  };

  return (
    <FormPage
      backTo="/rentals"
      backLabel="Voltar aos aluguéis"
      breadcrumb="Patrimônio / Aluguéis / Novo"
      title="Novo aluguel"
      subtitle="Registrar novo contrato de locação"
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={() => navigate('/rentals')}>
            Cancelar
          </button>
          <button
            type="submit"
            form="rental-form"
            className="btn btn-primary"
            disabled={mutation.isLoading}
          >
            {mutation.isLoading ? 'Salvando…' : 'Salvar aluguel'}
          </button>
        </>
      }
    >
      {errors.submit && <div className="field-error" style={{ marginBottom: 12 }}>{errors.submit}</div>}
      <form id="rental-form" noValidate onSubmit={onSubmit}>
        <div className="form-section-title">Contrato</div>
        <div className="form-grid">
          <label className="field full">
            <span>Bem / imóvel *</span>
            <select
              className="select"
              value={assetId}
              onChange={e => setAssetId(e.target.value)}
            >
              <option value="">Selecione…</option>
              {(assets || []).map((a: any) => (
                <option key={a.id} value={a.id}>
                  {a.description}
                </option>
              ))}
            </select>
            {errors.assetId && <div className="field-error">{errors.assetId}</div>}
          </label>
          <label className="field">
            <span>Inquilino *</span>
            <input className="input" value={tenantName} onChange={e => setTenantName(e.target.value)} />
            {errors.tenantName && <div className="field-error">{errors.tenantName}</div>}
          </label>
          <label className="field">
            <span>Aluguel mensal *</span>
            <input
              className="input"
              value={monthlyRentDisplay}
              onChange={e => setMonthlyRentDisplay(applyCurrencyMask(e.target.value))}
              placeholder="R$ 0,00"
            />
            {errors.monthlyRent && <div className="field-error">{errors.monthlyRent}</div>}
          </label>
          <label className="field">
            <span>E-mail</span>
            <input className="input" type="email" value={tenantEmail} onChange={e => setTenantEmail(e.target.value)} />
          </label>
          <label className="field">
            <span>Telefone</span>
            <input className="input" value={tenantPhone} onChange={e => setTenantPhone(e.target.value)} />
          </label>
          <label className="field">
            <span>Dia de vencimento</span>
            <input
              className="input"
              type="number"
              min={1}
              max={31}
              value={paymentDay}
              onChange={e => setPaymentDay(e.target.value)}
            />
          </label>
          <label className="field">
            <span>Início *</span>
            <input className="input" type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
            {errors.startDate && <div className="field-error">{errors.startDate}</div>}
          </label>
        </div>
      </form>
    </FormPage>
  );
};

export default CreateRentalGlobal;
